/* ==========================================================================
   ZETA SEARCH APIクライアント
   ==========================================================================

   Zeta API の呼び出しを一元化する薄いクライアント。fetch を書くのはこのファイルだけ。
   接続先は window.zetaSearchConfig(zeta-search-config.liquid)で切り替える。

   API仕様書: docs/Zeta-Search/APIDocs/
   ========================================================================== */

window.ZetaSearchAPI = (() => {
  const config = window.zetaSearchConfig || {};

  /**
   * params の値の型ルール(商品検索API仕様書「入力形式」準拠):
   * - スカラー             → key=value
   * - 配列(文字列[])       → key=v1,v2 (カンマ結合、OR条件)
   * - 二重配列(文字列[][]) → key=v1,v2&key=v3 (複数回指定、指定間はAND)
   * - null/undefined/''   → 送らない
   */
  function buildUrl(path, params, baseUrl) {
    const url = new URL(path, baseUrl || config.baseUrl);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value)) {
        if (value.length === 0) return;
        if (Array.isArray(value[0])) {
          value.forEach((inner) => inner.length && url.searchParams.append(key, inner.join(',')));
        } else {
          url.searchParams.append(key, value.join(','));
        }
      } else {
        url.searchParams.append(key, value);
      }
    });
    return url;
  }

  async function request(path, { params, method = 'GET', body, signal, baseUrl } = {}) {
    // POST/DELETE の Content-Type は application/json。JSONボディで送る
    // (URLSearchParams 形式は不可。MultiLikes 実装時にも同種の落とし穴があった)
    const response = await fetch(buildUrl(path, params, baseUrl), {
      method,
      signal,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) {
      const error = new Error(`[zeta-search] ${method} ${path} -> ${response.status}`);
      error.status = response.status;
      error.body = await response.json().catch(() => null); // 400時のバリデーション詳細
      throw error;
    }
    // 履歴削除 DELETE は成功時 204/ボディ無し。json() しない
    if (response.status === 204) return null;
    return response.json();
  }

  /* ------------------------------------------------------------
     カード補完データ(価格・在庫・ラベル)の取得 — 実装仕様書 §3.3.1
     ------------------------------------------------------------
     Zeta API ではなく自ストアの検索JSONビュー(templates/search.zeta-cards.liquid)を
     叩くため、buildUrl(config.baseUrl を前置する)は通さない。
     価格の計算・整形はすべてテンプレート側で完結しており、ここは取得のみを担う。
     ------------------------------------------------------------ */

  // 取得単位。テンプレートの paginate 上限(250)と Shopify 検索クエリ長の安全マージンを兼ねる。
  // 通常(120件/ページ)は分割されず1リクエストで完結する。
  // 実環境でURL長が問題になった場合はこの値だけ下げれば呼び出し側の変更は不要。
  const CARD_DATA_CHUNK_SIZE = 250;

  async function fetchCardData(numericIds, signal) {
    const chunks = [];
    for (let i = 0; i < numericIds.length; i += CARD_DATA_CHUNK_SIZE) {
      chunks.push(numericIds.slice(i, i + CARD_DATA_CHUNK_SIZE));
    }

    const responses = await Promise.all(chunks.map((chunk) => {
      const q = chunk.map((id) => `id:${id}`).join(' OR ');
      // options[unavailable_products]=show は必須。省略すると在庫切れ商品が検索結果から
      // 欠落し、SOLD OUT表示ができなくなる(custom-wishlist.js と同じ指定)
      const url = `${config.searchUrl}?view=zeta-cards&type=product`
        + `&options%5Bunavailable_products%5D=show&q=${encodeURIComponent(q)}`;

      return fetch(url, { signal, headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) {
          const error = new Error(`[zeta-search] GET ${url} -> ${response.status}`);
          error.status = response.status;
          throw error;
        }
        return response.json();
      });
    }));

    const map = new Map();
    responses.forEach((response) => {
      (response.products || []).forEach((product) => map.set(String(product.id), product));
    });
    return map;
  }

  // 履歴系(/api/user/*)は検索系(/api/search/*)とドメインが異なる(Recommend系)ため、
  // ベースURLを recommendBaseUrl に差し替えて呼ぶ
  const requestRecommend = (path, options = {}) =>
    request(path, { ...options, baseUrl: config.recommendBaseUrl });

  const api = {
    // 検索系(呼び出し元で AbortController を渡し、旧リクエストを abort する)
    searchProducts: (params, signal) => request('/api/search/product', { params, signal }),
    suggest: (q, l, signal) => request('/api/search/suggest', { params: { q, l }, signal }),
    hotwords: (l, signal) => request('/api/search/hotword', { params: { l }, signal }),
    didYouMean: (q, s, signal) => request('/api/search/didyoumean', { params: { q, s }, signal }),

    // カード補完データ(Shopify検索JSONビュー。§3.3.1)
    getCardData: fetchCardData,

    // キーワード履歴(Recommend系・すべてログイン時のみ呼ぶこと)
    getKeywordHistory: (userId) => requestRecommend('/api/user/keyword_history', { params: { user_id: userId } }),
    saveKeywordHistory: (userId, keyword, category) =>
      requestRecommend('/api/user/keyword_history', { method: 'POST', body: { user_id: userId, keyword, category: category || null } }),
    deleteKeywordHistory: (id) => requestRecommend(`/api/user/keyword_history/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    clearKeywordHistory: (userId) => requestRecommend('/api/user/keyword_history', { method: 'DELETE', params: { user_id: userId } }),

    // 絞り込み履歴(Recommend系・すべてログイン時のみ呼ぶこと)
    getFilterHistory: (userId, category) => requestRecommend('/api/user/filter_history', { params: { user_id: userId, category } }),
    saveFilterHistory: (userId, category, filters) =>
      requestRecommend('/api/user/filter_history', { method: 'POST', body: { user_id: userId, category, filters } }),
    deleteFilterHistory: (id) => requestRecommend(`/api/user/filter_history/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    // カテゴリ閲覧履歴(Recommend系・ログイン時のみ呼ぶこと)
    getCategoryHistory: (userId) => requestRecommend('/api/user/category_history', { params: { user_id: userId } })
  };

  return api;
})();
